import { useCallback, useEffect, useState } from 'react'
import { ShieldAlert, UserX, Check, X, ChevronDown } from 'lucide-react'
import { apiGet, adminMutate, useToast } from '../admin/primitives.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { useMenuPersonne } from './menu-personne.js'

/**
 * /rh-access/reconciliation — associer les identités Skello ↔ UniFi Access
 * (LOT 7). TOUTES les décisions restent DANS Gestion : aucun compte Skello
 * ou UniFi n'est modifié, jamais — la modal le rappelle (§104).
 */

interface UnifiLite {
  external_id: string
  first_name: string
  last_name: string
  email: string | null
  employee_number: string | null
  status: string | null
  policies: { id: string; name: string }[]
  nfc_count: number
  missing: boolean
}
interface PersonLite { person_id: string; display_name: string; skello_id: string | null; skello_missing: boolean }
interface View {
  linked: { person: PersonLite; unifi: UnifiLite; nameDrift: boolean }[]
  proposals: { unifi: UnifiLite; candidates: { person: PersonLite; confidence: 'TRES_FORTE' | 'FORTE' | 'A_VERIFIER'; signals: string[] }[]; ambiguous: boolean }[]
  unmatchedUnifi: UnifiLite[]
  personsWithoutAccess: PersonLite[]
  conflicts: { kind: string; message: string }[]
}

const CONF_LABEL = { TRES_FORTE: 'Très forte', FORTE: 'Forte', A_VERIFIER: 'À vérifier' } as const
const CONF_CLS = { TRES_FORTE: 'lc-pill--ok', FORTE: 'lc-pill--ok', A_VERIFIER: 'lc-pill--warning' } as const

export function SkelloReconciliationSection() {
  const [view, setView] = useState<View | null>(null)
  const [confirm, setConfirm] = useState<{ unifi: UnifiLite; person: PersonLite; confidence: string; signals: string[] } | null>(null)
  const [unlink, setUnlink] = useState<{ unifi: UnifiLite; person: PersonLite } | null>(null)
  const [showLinked, setShowLinked] = useState(false)
  const toast = useToast()
  const menu = useMenuPersonne()

  const charger = useCallback(() => {
    apiGet<View>('/api/v1/rh/reconciliation')
      .then((v) => { if ('proposals' in v) setView(v) })
      .catch(() => undefined)
  }, [])
  useEffect(charger, [charger])

  const lier = async () => {
    if (!confirm) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/rh/reconciliation/link', {
      person_id: confirm.person.person_id, external_id: confirm.unifi.external_id,
      confidence: confirm.confidence === 'TRES_FORTE' ? 'CERTAIN' : confirm.confidence === 'FORTE' ? 'PROBABLE' : 'MANUEL',
      signals: confirm.signals,
    })
    setConfirm(null)
    if (r.status < 300) { toast('ok', 'Association confirmée — aucun compte externe modifié.'); charger() }
    else toast('error', r.data.message ?? 'L’association a été refusée.')
  }

  const delier = async () => {
    if (!unlink) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/rh/reconciliation/unlink', { external_id: unlink.unifi.external_id })
    setUnlink(null)
    if (r.status < 300) { toast('ok', 'Association retirée.'); charger() }
    else toast('error', r.data.message ?? 'Le retrait a été refusé.')
  }

  const ecarter = async (externalId: string, personId: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/rh/reconciliation/dismiss', {
      external_id: externalId, decision: 'PAS_LA_MEME_PERSONNE', person_id: personId,
    })
    if (r.status < 300) { toast('ok', 'Noté : ce n’est pas la même personne.'); charger() }
  }

  const ignorer = async (externalId: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/rh/reconciliation/dismiss', {
      external_id: externalId, decision: 'IGNORER',
    })
    if (r.status < 300) { toast('ok', 'Compte marqué à ignorer (technique / hors RH).'); charger() }
  }

  return (
    <div className="lc-stagger">
      <p className="lc-subtitle">
        Associer les personnes Skello aux comptes UniFi Access. Les décisions restent
        dans Gestion — aucun compte Skello ou UniFi n’est jamais modifié.
      </p>

      {/* ─── Conflits ─── */}
      {view && view.conflicts.length > 0 ? (
        <section className="lc-panel gd-panel rh-attn" aria-label="Conflits">
          <header className="lc-panel__head"><h2 className="lc-section-title"><ShieldAlert size={16} aria-hidden="true" /> Conflits</h2></header>
          <div className="lc-panel__body">
            {view.conflicts.map((c, i) => (
              <p key={i} className="rh-conflict">{c.message}</p>
            ))}
          </div>
        </section>
      ) : null}

      {/* ─── Associations proposées ─── */}
      <section className="lc-panel gd-panel" aria-label="Associations proposées">
        <header className="lc-panel__head">
          <h2 className="lc-section-title">Associations proposées</h2>
          <span className="lc-hint">{view ? `${view.proposals.length} proposition${view.proposals.length > 1 ? 's' : ''}` : '…'}</span>
        </header>
        <div className="lc-panel__body">
          {view && view.proposals.length === 0 ? <p className="lc-hint">Rien à proposer — tout est associé ou écarté.</p> : null}
          {(view?.proposals ?? []).map((prop) => (
            <div key={prop.unifi.external_id} className="rh-match">
              <div className="rh-match__cols">
                <div className="rh-match__col">
                  <span className="rh-match__source">UniFi Access</span>
                  <span className="rh-match__name">{prop.unifi.first_name} {prop.unifi.last_name}</span>
                  <span className="lc-hint">
                    {prop.unifi.status === 'ACTIVE' ? 'Compte actif' : `Compte ${prop.unifi.status?.toLowerCase() ?? 'inconnu'}`}
                    {prop.unifi.policies.length > 0 ? ` · ${prop.unifi.policies.map((p) => p.name).join(', ')}` : ''}
                  </span>
                </div>
                <div className="rh-match__mid" aria-hidden="true">
                  <span className={`lc-pill ${CONF_CLS[prop.candidates[0]!.confidence]}`}>{CONF_LABEL[prop.candidates[0]!.confidence]}</span>
                </div>
                <div className="rh-match__col">
                  <span className="rh-match__source">Skello</span>
                  {prop.candidates.map((c) => (
                    <div key={c.person.person_id} className="rh-cand">
                      <span className="lc-stack">
                        <span className="rh-match__name">{c.person.display_name}</span>
                        <span className="lc-hint">{c.signals.join(' · ')}</span>
                      </span>
                      <span className="rh-cand__actions">
                        <button className="lc-btn lc-btn--primary" onClick={() => setConfirm({ unifi: prop.unifi, person: c.person, confidence: c.confidence, signals: c.signals })}>
                          <Check size={14} aria-hidden="true" /> Associer
                        </button>
                        <button className="lc-btn lc-btn--ghost" title="Ce n’est pas la même personne" onClick={() => void ecarter(prop.unifi.external_id, c.person.person_id)}>
                          <X size={14} aria-hidden="true" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Accès sans personne RH ─── */}
      <section className="lc-panel gd-panel" aria-label="Accès sans personne RH" id="orphelins">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><UserX size={16} aria-hidden="true" /> Accès sans personne RH associée</h2>
          <span className="lc-hint">{view ? `${view.unmatchedUnifi.filter((u) => !u.missing).length} comptes` : '…'}</span>
        </header>
        <div className="lc-panel__body">
          <p className="lc-hint" style={{ marginTop: 0 }}>
            Comptes UniFi Access sans correspondance Skello : anciens salariés,
            comptes techniques, prestataires… Marquer « à ignorer » sort le compte
            des propositions — rien n’est modifié dans UniFi Access.
          </p>
          <div className="rh-orphans">
            {(view?.unmatchedUnifi ?? []).filter((u) => !u.missing).map((u) => (
              <div key={u.external_id} className="rh-orphan">
                <span className="lc-stack">
                  <span>{u.first_name} {u.last_name}</span>
                  <span className="lc-hint">{u.status === 'ACTIVE' ? 'Actif' : u.status ?? '—'}{u.nfc_count > 0 ? ` · ${u.nfc_count} badge${u.nfc_count > 1 ? 's' : ''}` : ''}</span>
                </span>
                <button className="lc-btn lc-btn--ghost" onClick={() => void ignorer(u.external_id)}>À ignorer</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Associés ─── */}
      <section className="lc-panel gd-panel" aria-label="Associations confirmées">
        <header className="lc-panel__head">
          <button className="lc-section-title lc-btn--bare" onClick={() => setShowLinked(!showLinked)} aria-expanded={showLinked}>
            Associations confirmées <ChevronDown size={15} style={{ transform: showLinked ? 'rotate(180deg)' : undefined }} aria-hidden="true" />
          </button>
          <span className="lc-hint">{view ? `${view.linked.length}` : '…'}</span>
        </header>
        {showLinked ? (
          <div className="lc-panel__body">
            {(view?.linked ?? []).map((l) => (
              <div key={l.unifi.external_id} className="rh-orphan" {...menu.personne({ id: l.person.person_id, nom: l.person.display_name, accesLie: true })}>
                <span className="lc-stack">
                  <span>{l.person.display_name} <span className="lc-hint">↔ {l.unifi.first_name} {l.unifi.last_name} (UniFi)</span></span>
                  {l.nameDrift ? <span className="lc-pill lc-pill--warning">Noms divergents — signalé, jamais corrigé automatiquement</span> : null}
                </span>
                <button className="lc-btn lc-btn--ghost" onClick={() => setUnlink({ unifi: l.unifi, person: l.person })}>Retirer</button>
              </div>
            ))}
            {view && view.linked.length === 0 ? <p className="lc-hint">Aucune association confirmée pour l’instant.</p> : null}
          </div>
        ) : null}
      </section>

      <ConfirmDialog
        open={confirm !== null}
        danger={false}
        title="Associer ces identités ?"
        confirmLabel="Confirmer l’association"
        onConfirm={() => void lier()}
        onClose={() => setConfirm(null)}
      >
        {confirm ? (
          <>Cette action indique à Gestion que <strong>{confirm.person.display_name}</strong> (Skello)
          et <strong>{confirm.unifi.first_name} {confirm.unifi.last_name}</strong> (UniFi Access)
          représentent la même personne. Aucun compte UniFi ou Skello ne sera modifié.</>
        ) : null}
      </ConfirmDialog>

      <ConfirmDialog
        open={unlink !== null}
        danger
        title="Retirer l’association ?"
        confirmLabel="Retirer"
        onConfirm={() => void delier()}
        onClose={() => setUnlink(null)}
      >
        {unlink ? (
          <>Gestion oubliera que <strong>{unlink.person.display_name}</strong> correspond à ce compte
          UniFi Access. Une confirmation forte vous sera demandée. Aucun compte externe n’est modifié.</>
        ) : null}
      </ConfirmDialog>
    </div>
  )
}
